<script lang="ts">
import { useI18n } from 'vue-i18n';
import { ValidationRule } from 'quasar';
import { defineComponent, PropType } from 'vue';

import { AccountsWithCurrency } from '@/shared/services/account';

import UiSelect from '@/shared/ui/select';
import UiPlaceholderEmpty from '@/shared/ui/placeholder/empty';
import ObChip from '@/shared/ui/ob-chip';
import { AccountClassification } from '@/app/graphql/account/sdk.account';
import { useAccountEmptyLabel } from '@/shared/lib/use-account-empty-label';

export default defineComponent({
  name: 'EntityAccountWithCurrency',
  components: { ObChip, UiSelect, UiPlaceholderEmpty },
  props: {
    modelValue: {
      type: Object as PropType<AccountsWithCurrency | null>,
      default: null,
    },
    options: {
      type: Array as PropType<AccountsWithCurrency[]>,
      default: () => [],
    },
    rules: {
      type: Array as PropType<ValidationRule[]>,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    currency: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    lazyRules: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    useInput: {
      type: Boolean,
      default: false,
    },
    emitValue: {
      type: Boolean,
      default: false,
    },
    inputDebounce: {
      type: String,
      default: '0',
    },
  },
  emits: ['update:modelValue', 'filter'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { accountEmptyLabel } = useAccountEmptyLabel();
    const onFilter = (val: string, update: Function) => {
      update();
      emit('filter', val, update);
    };
    return {
      onFilter,
      t,
      accountEmptyLabel,
      AccountClassification,
    };
  },
});
</script>

<template>
  <ui-select
    :model-value="modelValue"
    :options="options"
    :label="label"
    :loading="loading"
    :emit-value="emitValue"
    :option-label="optionLabel"
    :option-value="optionValue"
    :input-debounce="inputDebounce"
    :clearable="clearable"
    :use-input="useInput"
    :disable="disable"
    :rules="rules"
    :lazy-rules="lazyRules"
    @update:model-value="$emit('update:modelValue', $event)"
    @filter="onFilter"
  >
    <template #option-body="opt">
      <div class="row align-items-center">
        <span>{{ opt?.account }}</span>
        <ob-chip v-if="opt.accountClassification === AccountClassification.Islamic" label="Islamic" class="ob-ml-sm" />
      </div>
    </template>

    <template #selected-item="scope">
      <div class="row">
        <span>{{ scope?.opt.account }}</span>
        <div>
          <ob-chip
            v-if="scope?.opt.accountClassification === AccountClassification.Islamic"
            label="Islamic"
            class="ob-ml-sm"
          />
        </div>
      </div>
    </template>

    <template #no-option>
      <ui-placeholder-empty :label="accountEmptyLabel" size="50" />
    </template>
  </ui-select>
</template>
